import { useBrand } from '@/hooks/useBrand'
import { cn } from '@/lib/utils'

interface WordmarkProps {
  /** Optional product label rendered after the brand, e.g. "RMS". */
  suffix?: string
  /** Explicit brand override (marketing pages pass their `brand` prop); defaults to the shared prop. */
  brand?: string
  className?: string
  suffixClassName?: string
}

/**
 * The brand lockup. "zewzew" is all x-height letters — no ascenders, no descenders —
 * so the lowercase word already reads as one uniform band. The suffix ("RMS") is set in
 * caps at 0.72em: DM Sans cap height ≈ 0.72em and x-height ≈ 0.52em, so a 0.72em suffix
 * has a cap height of ≈ 0.52em of the parent — exactly the brand's x-height. Everything
 * in "zewzew RMS" reads as the same height while keeping lowercase letterforms.
 *
 * Both sit on a shared baseline (items-baseline); tune per-surface via suffixClassName.
 */
export function Wordmark({ suffix, brand: brandProp, className, suffixClassName }: WordmarkProps) {
  const sharedBrand = useBrand()
  const brand = brandProp ?? sharedBrand
  return (
    <span className={cn('inline-flex items-baseline whitespace-nowrap leading-none', className)}>
      <span className="lowercase tracking-[-0.015em]">{brand}</span>
      {suffix ? (
        <span className={cn('ml-[0.4em] text-[0.72em] font-semibold uppercase tracking-[0.14em]', suffixClassName)}>
          {suffix}
        </span>
      ) : null}
    </span>
  )
}
